import type { layout } from "@app/domain";
import type { Variant } from "@app/protocol";
import { type ReactNode, useEffect, useState } from "react";
import { shareTable } from "../discord/handshake";
import { useT } from "../i18n/context";
import { useStore } from "../store";
import { leaveTable, openTable, send } from "../ws/singleton";
import { IconButton } from "./IconButton";
import { Icon } from "./icons";
import { ReactionControl } from "./ReactionControl";
import { RulesButton } from "./RulesLink";
import { SoundControl } from "./SoundControl";
import { Toast } from "./Toast";

export interface BackAction {
  label: string;
  onClick: () => void;
}

/**
 * Where Back goes (review 0012, decision 57, revised 13 Sept 2026): seated, it leaves the
 * table (in your own lobby too: standing up to watch bots was a trap); on another channel's
 * table it takes you home. From a matched table's lobby it gives the seat back first, so the queue
 * can fill it (review 0020).
 */
export function useBack(
  where: "lobby" | "table",
  seated: boolean,
  queue = false,
): BackAction | null {
  const { t } = useT();
  const tableId = useStore((s) => s.tableId);
  const instanceId = useStore((s) => s.instanceId);
  const home =
    tableId !== null && instanceId !== null && tableId !== instanceId ? instanceId : null;
  if (seated && (where === "table" || home === null))
    return { label: t("table.leave"), onClick: leaveTable };
  if (home === null) return null;
  const giveBack = queue && seated && where === "lobby";
  return {
    label: t("lobby.leaveTable"),
    onClick: () => {
      if (giveBack) send({ type: "leave" });
      openTable(home);
    },
  };
}

const INVITE_TOAST_MS = 2_600;

/** Discord's share sheet inside Discord; outside it the link goes to the clipboard with a toast (U14). */
function useInvite(): { invite: (() => void) | null; copied: boolean } {
  const { t } = useT();
  const sdk = useStore((s) => s.sdk);
  const tableId = useStore((s) => s.tableId);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), INVITE_TOAST_MS);
    return () => clearTimeout(id);
  }, [copied]);
  if (tableId === null) return { invite: null, copied };
  const invite = () => void shareTable(sdk, tableId, t("lobby.share.message")).then(setCopied);
  return { invite, copied };
}

function Watchers({ count }: { count: number }) {
  const { t } = useT();
  const label = t("lobby.watchers", { count });
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      className="flex h-9 items-center gap-1.5 rounded-pill border-[1.5px] border-edge bg-panel px-3 text-[13px] font-bold backdrop-blur-[10px]"
    >
      <Icon name="eye" size={18} />
      <span aria-hidden="true">{count}</span>
    </span>
  );
}

const FRAME: Record<layout.LayoutState, { button: number; left: string; info: string }> = {
  wide: {
    button: 56,
    left: "left-5 top-4 gap-3.5",
    info: "left-1/2 top-4 -translate-x-1/2 flex-col",
  },
  portrait: {
    button: 44,
    left: "left-3 top-2 gap-2.5",
    info: "left-1/2 top-[54px] -translate-x-1/2 flex-col",
  },
  phoneLandscape: { button: 44, left: "left-3 top-1 gap-2.5", info: "right-3 top-1 flex-row" },
};

/** Back, invite and watchers on the left; the game's chips in the middle (right on a landscape phone). */
export function TopBar({
  state,
  back,
  watchers,
  info,
  seated = false,
  variant,
}: {
  state: layout.LayoutState;
  back: BackAction | null;
  watchers: number;
  info?: ReactNode;
  /** The game being played, for its rules page (decision 174). */
  variant?: Variant;
  /** Seated players react to the table (review 0022); spectators only watch. */
  seated?: boolean;
}) {
  const { t } = useT();
  const frame = FRAME[state];
  const { invite, copied } = useInvite();
  return (
    <div className="relative h-full">
      <div className={`absolute flex items-center ${frame.left}`}>
        {back ? (
          <IconButton label={back.label} size={frame.button} onClick={back.onClick}>
            <Icon name="back" strokeWidth={2.4} />
          </IconButton>
        ) : null}
        {invite ? (
          <IconButton label={t("tooltip.invite")} size={frame.button} onClick={invite}>
            <Icon name="invite" />
          </IconButton>
        ) : null}
        {variant ? <RulesButton variant={variant} size={frame.button} /> : null}
        <SoundControl size={frame.button} />
        {seated ? <ReactionControl size={frame.button} /> : null}
        {watchers > 0 ? <Watchers count={watchers} /> : null}
      </div>
      {info ? (
        <div className={`absolute flex w-max items-center gap-1.5 ${frame.info}`}>{info}</div>
      ) : null}
      {copied ? (
        <div className="absolute inset-x-0 top-full z-30 h-16">
          <Toast
            toast={{ title: t("invite.copied"), sub: t("invite.copiedSub"), tone: "success" }}
            dense={state !== "wide"}
          />
        </div>
      ) : null}
    </div>
  );
}
